import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc, collection, getDocs, query, where, orderBy, limit, startAfter } from 'firebase/firestore';
import Swal from 'sweetalert2';
import AdminLayout from './AdminLayout';
import './AdminDashboard.css';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const SalesLineChart = ({ data }) => {
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: '8px', padding: '8px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#333' }}>${payload[0].value.toFixed(2)}</div>
          <div style={{ fontSize: '11px', color: '#999' }}>{label}</div>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ width: '100%', height: 260 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
          <defs>
            <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#ff6b35" stopOpacity={0.1} />
              <stop offset="95%" stopColor="#ff6b35" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} stroke="#f0f0f0" />
          <XAxis
            dataKey="date"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: '#999' }}
            tickFormatter={(value) => {
              const parts = value.split('-');
              return `${parts[1]}/${parts[2]}`;
            }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: '#999' }}
            tickFormatter={(value) => `$${value.toFixed(0)}`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="amount"
            stroke="#ff6b35"
            strokeWidth={3}
            fill="url(#colorSales)"
            dot={{ r: 4, fill: '#fff', stroke: '#ff6b35', strokeWidth: 2 }}
            activeDot={{ r: 6, fill: '#fff', stroke: '#ff6b35', strokeWidth: 2 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const LOC_COLORS = ['#ff6b35', '#0ea5e9', '#22c55e', '#8b5cf6', '#eab308'];

/** Multi-location sales chart: one line per selected location (recharts). */
const SalesMultiLineChart = ({ chartByLoc, locationIds }) => {
  // Merge per-location series into rows: { date, [locId]: amount, ... }
  const byDate = {};
  for (const locId of locationIds) {
    for (const point of (chartByLoc[locId] || [])) {
      if (!byDate[point.date]) byDate[point.date] = { date: point.date };
      byDate[point.date][locId] = point.amount;
    }
  }
  const data = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: '8px', padding: '8px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          {payload.map((p, i) => (
            <div key={i} style={{ fontSize: '12px', fontWeight: 600, color: p.stroke }}>${Number(p.value || 0).toFixed(2)}</div>
          ))}
          <div style={{ fontSize: '11px', color: '#999' }}>{label}</div>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ width: '100%', height: 260 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="#f0f0f0" />
          <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#999' }}
            tickFormatter={(value) => { const parts = value.split('-'); return `${parts[1]}/${parts[2]}`; }} />
          <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#999' }} tickFormatter={(value) => `$${value.toFixed(0)}`} />
          <Tooltip content={<CustomTooltip />} />
          {locationIds.map((locId, i) => (
            <Area
              key={locId}
              type="monotone"
              dataKey={locId}
              stroke={LOC_COLORS[i % LOC_COLORS.length]}
              strokeWidth={3}
              fill="none"
              dot={{ r: 3, fill: '#fff', stroke: LOC_COLORS[i % LOC_COLORS.length], strokeWidth: 2 }}
              connectNulls
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const AdminDashboardPage = () => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  // Multi-location metric combination (dashboard-scoped): the first id is the
  // working location (drives the rest of the page); extra ids only combine
  // the dashboard metrics + chart.
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);
  const [statsByLoc, setStatsByLoc] = useState({}); // { locId: {orders, sales, avgTicket, itemsSold} }
  const [chartByLoc, setChartByLoc] = useState({});  // { locId: [{date, amount}] }
  const [chartMode, setChartMode] = useState('combined'); // 'combined' | 'individual'
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  
  // Stats
  const [todayOrders, setTodayOrders] = useState(0);
  const [todaySales, setTodaySales] = useState(0);
  const [avgTicketSize, setAvgTicketSize] = useState(0);
  const [totalItemsSold, setTotalItemsSold] = useState(0);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Sales Chart
  const [timeFilter, setTimeFilter] = useState('last3days');
  const [salesData, setSalesData] = useState([]);
  const [isLoadingChart, setIsLoadingChart] = useState(false);
  
  // Recent Orders
  const [recentOrders, setRecentOrders] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMoreOrders, setHasMoreOrders] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadUserData(currentUser.uid);
      } else {
        navigate('/admin/login');
      }
    });

    return () => unsubscribe();
  }, []);

  // Load sales data when selected location changes
  useEffect(() => {
    if (selectedLocation && user && selectedLocationIds.length > 0) {
      loadSalesData();
      loadStats();
      loadRecentOrders(true);
    }
  }, [selectedLocation, timeFilter, selectedLocationIds]);

  const loadUserData = async (uid) => {
    try {
      const db = getFirestore();
      
      // Load user data
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const uData = userDoc.data();
        setUserData(uData);
        
        // Load locations
        const locsSnapshot = await getDocs(collection(db, 'users', uid, 'locations'));
        const locs = locsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setLocations(locs);
        
        // Set selected location
        const selectedId = uData.selectedLocationId;
        if (selectedId && locs.length > 0) {
          const selected = locs.find(l => l.id === selectedId);
          const loc = selected || locs[0];
          setSelectedLocation(loc);
          setSelectedLocationIds([loc.id]);
        } else if (locs.length > 0) {
          setSelectedLocation(locs[0]);
          setSelectedLocationIds([locs[0].id]);
          // Save first location as selected
          await saveSelectedLocation(uid, locs[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSelectedLocation = async (uid, locationId) => {
    try {
      const db = getFirestore();
      await getDoc(doc(db, 'users', uid)); // Just to check if exists
      // We need to update the user document with selectedLocationId
      // Using setDoc with merge to update only selectedLocationId
      const { setDoc: setDocFn, doc: docFn } = await import('firebase/firestore');
      await setDocFn(docFn(db, 'users', uid), { selectedLocationId: locationId }, { merge: true });
    } catch (error) {
      console.error('Error saving selected location:', error);
    }
  };

  const handleSelectLocation = async (location) => {
    if (!user) return;

    setSelectedLocation(location);
    setSelectedLocationIds([location.id]);
    setShowLocationPicker(false);

    try {
      await saveSelectedLocation(user.uid, location.id);
    } catch (error) {
      console.error('Error saving selected location:', error);
    }
  };

  // Multi-select toggle for the dashboard metrics: first id = working
  // location (drives the whole page); extras only combine the metrics.
  const toggleDashboardLocation = (location) => {
    setSelectedLocationIds((prev) => {
      const has = prev.includes(location.id);
      let next;
      if (has) {
        next = prev.filter((id) => id !== location.id);
        if (next.length === 0) return prev; // never zero locations
      } else {
        next = [...prev, location.id];
      }
      // Keep the working location as the first id (and re-point it if the
      // current working one got removed from the selection).
      if (!next.includes(selectedLocation?.id)) {
        const newWorking = locations.find((l) => l.id === next[0]);
        if (newWorking) {
          setSelectedLocation(newWorking);
          if (user) saveSelectedLocation(user.uid, newWorking.id);
        }
      }
      return next;
    });
  };

  const loadStats = async () => {
    if (!user || !selectedLocation || selectedLocationIds.length === 0) return;

    setStatsLoading(true);

    try {
      const db = getFirestore();
      const now = new Date();
      let startDate;

      switch (timeFilter) {
        case 'today':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case 'last7days':
          startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 7);
          break;
        case 'last30days':
          startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 30);
          break;
        case 'last45days':
          startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 45);
          break;
        default:
          startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 3);
      }

      // Same query per selected location, combined into the legacy setters
      // (single-location behavior is byte-identical to before).
      const perLoc = await Promise.all(selectedLocationIds.map(async (locId) => {
        const salesRef = collection(db, 'users', user.uid, 'sales');
        const periodQuery = query(
          salesRef,
          where('locationId', '==', locId),
          where('orderDate', '>=', startDate)
        );
        const periodSnapshot = await getDocs(periodQuery);
        let orders = 0;
        let sales = 0;
        let itemsSold = 0;
        periodSnapshot.forEach(doc => {
          const data = doc.data();
          orders++;
          sales += data.total || 0;
          itemsSold += data.itemsQty || 0;
        });
        return [locId, { orders, sales, avgTicket: orders > 0 ? sales / orders : 0, itemsSold }];
      }));

      const byLoc = Object.fromEntries(perLoc);
      setStatsByLoc(byLoc);
      const orders = perLoc.reduce((a, [, v]) => a + v.orders, 0);
      const sales = perLoc.reduce((a, [, v]) => a + v.sales, 0);
      const itemsSold = perLoc.reduce((a, [, v]) => a + v.itemsSold, 0);
      setTodayOrders(orders);
      setTodaySales(sales);
      setAvgTicketSize(orders > 0 ? sales / orders : 0);
      setTotalItemsSold(itemsSold);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const loadSalesData = async () => {
    if (!user || !selectedLocation) return;
    
    setIsLoadingChart(true);
    
    try {
      const db = getFirestore();
      const now = new Date();
      let daysCount;
      
      switch (timeFilter) {
        case 'today': daysCount = 1; break;
        case 'last7days': daysCount = 7; break;
        case 'last30days': daysCount = 30; break;
        case 'last45days': daysCount = 45; break;
        default: daysCount = 3;
      }
      
      const startDate = new Date(now);
      startDate.setDate(startDate.getDate() - daysCount);
      
      // Initialize daily sales with 0 — LOCAL date keys (merchant's day),
      // never UTC: toISOString() buckets by UTC date, which for UTC−N
      // merchants counts yesterday-evening sales as "today" (prod report:
      // chart said $1,930 vs the real local-today $346).
      const localDateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const dailySales = {};
      for (let i = 0; i < daysCount; i++) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        dailySales[localDateKey(date)] = 0;
      }
      
      // Fetch sales per selected location (combined array stays identical
      // when a single location is selected)
      const perLocSeries = await Promise.all(selectedLocationIds.map(async (locId) => {
        const salesRef = collection(db, 'users', user.uid, 'sales');
        const salesQuery = query(
          salesRef,
          where('locationId', '==', locId),
          where('orderDate', '>=', startDate)
        );
        const snapshot = await getDocs(salesQuery);
        const locDaily = { ...dailySales };
        snapshot.forEach(doc => {
          const data = doc.data();
          const orderDate = data.orderDate?.toDate ? data.orderDate.toDate() : new Date(data.orderDate);
          const dateKey = localDateKey(orderDate); // merchant-local day

          if (locDaily.hasOwnProperty(dateKey)) {
            locDaily[dateKey] += data.total || 0;
          }
        });
        return [locId, Object.entries(locDaily)
          .map(([date, amount]) => ({ date, amount: Math.round(amount * 100) / 100 }))
          .sort((a, b) => a.date.localeCompare(b.date))];
      }));

      const byLoc = Object.fromEntries(perLocSeries);
      setChartByLoc(byLoc);
      // Combined = the sum per day across all selected locations
      const combined = { ...dailySales };
      for (const series of Object.values(byLoc)) {
        for (const point of series) combined[point.date] = (combined[point.date] || 0) + point.amount;
      }
      const chartData = Object.entries(combined)
        .map(([date, amount]) => ({ date, amount: Math.round(amount * 100) / 100 }))
        .sort((a, b) => a.date.localeCompare(b.date));
      setSalesData(chartData);
    } catch (error) {
      console.error('Error loading sales data:', error);
    } finally {
      setIsLoadingChart(false);
    }
  };

  const loadRecentOrders = async (reset = false) => {
    if (!user || !selectedLocation) return;
    
    setLoadingOrders(true);
    
    try {
      const db = getFirestore();
      const salesRef = collection(db, 'users', user.uid, 'sales');
      
      const now = new Date();
      let startDate;
      switch (timeFilter) {
        case 'today':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case 'last7days':
          startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 7);
          break;
        case 'last30days':
          startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 30);
          break;
        case 'last45days':
          startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 45);
          break;
        default:
          startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 3);
      }
      
      let ordersQuery;
      if (reset || !lastDoc) {
        ordersQuery = query(
          salesRef,
          where('locationId', '==', selectedLocation.id),
          where('orderDate', '>=', startDate),
          orderBy('orderDate', 'desc'),
          limit(10)
        );
      } else {
        ordersQuery = query(
          salesRef,
          where('locationId', '==', selectedLocation.id),
          where('orderDate', '>=', startDate),
          orderBy('orderDate', 'desc'),
          startAfter(lastDoc),
          limit(10)
        );
      }
      
      const snapshot = await getDocs(ordersQuery);
      
      if (snapshot.empty) {
        setHasMoreOrders(false);
        if (reset) setRecentOrders([]);
      } else {
          const orders = snapshot.docs.map(doc => {
          const data = doc.data();
          const orderDate = data.orderDate?.toDate ? data.orderDate.toDate() : new Date(data.orderDate);
          return {
            id: doc.id,
            orderNumber: data.orderNumber || doc.id.substring(0, 8),
            confirmationNumber: data.confirmationNumber || doc.id.substring(0, 8),
            guestNumber: data.guestNumber || '',
            customerName: data.customerName || data.customer?.name || 'Walk-in Customer',
            orderDate,
            status: data.status || 'completed',
            total: data.total || 0,
          };
        });
        
        if (reset) {
          setRecentOrders(orders);
        } else {
          setRecentOrders(prev => [...prev, ...orders]);
        }
        
        setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
        setHasMoreOrders(snapshot.docs.length === 10);
      }
    } catch (error) {
      console.error('Error loading recent orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTodayHours = () => {
    if (!selectedLocation?.operationHours) return 'Hours not set';
    
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const today = days[new Date().getDay()];
    const todayHours = selectedLocation.operationHours.filter(h => h.day === today);
    
    if (todayHours.length === 0) return 'Not set';
    if (todayHours.some(h => h.isClosed)) return 'Closed today';
    
    return todayHours.map(h => {
      const from = h.fromHour <= 12 ? `${h.fromHour || 12} AM` : `${h.fromHour - 12} PM`;
      const to = h.toHour <= 12 ? `${h.toHour || 12} AM` : `${h.toHour - 12} PM`;
      return `${from} - ${to}${h.isNextDay ? '+' : ''}`;
    }).join(', ');
  };

  if (loading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="text-center py-5">
          <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Dashboard">
      <div className="admin-dashboard">
        {/* Welcome Card */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="welcome-card" style={{
              background: 'linear-gradient(135deg, #ff6b35 0%, #ff8f35 100%)',
              borderRadius: '16px',
              padding: '20px',
              color: 'white',
            }}>
              <h4 className="m-0 font-weight-bold">Welcome, {userData?.name || user?.email?.split('@')[0] || 'User'}</h4>
            </div>
          </div>
        </div>

        {/* Location Selector */}
        {selectedLocation && (
          <div className="row mb-4">
            <div className="col-12">
              <div 
                className="location-selector-card"
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '20px',
                  boxShadow: '0 8px 30px rgba(255,107,53,0.15)',
                  cursor: 'pointer',
                }}
                onClick={() => setShowLocationPicker(true)}
              >
                <div className="d-flex align-items-center">
                  <div 
                    className="d-flex align-items-center justify-content-center mr-3"
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 12,
                      background: 'rgba(255,107,53,0.1)',
                      color: '#ff6b35',
                    }}
                  >
                    <i className="fas fa-store fa-lg"></i>
                  </div>
                  <div className="flex-grow-1">
                    <h5 className="m-0 font-weight-bold">{selectedLocation.companyName || selectedLocation.locationName}</h5>
                    <p className="m-0 text-muted" style={{ fontSize: '0.85rem' }}>
                      {selectedLocation.address || ''}, {selectedLocation.city || ''}
                    </p>
                  </div>
                  <div className="text-right">
                    <i className="fas fa-chevron-down text-muted"></i>
                  </div>
                </div>
                <div className="mt-3 pt-3" style={{ borderTop: '1px solid #f0f0f0' }}>
                  <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-clock mr-2"></i>
                    Today: {getTodayHours()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Time Filters */}
        {selectedLocation && (
          <div className="row mb-3">
            <div className="col-12">
              <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
                {['today', 'last3days', 'last7days', 'last30days', 'last45days'].map((filter) => (
                  <button
                    key={filter}
                    className={`btn btn-sm ${timeFilter === filter ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setTimeFilter(filter)}
                  >
                    {filter === 'today' ? 'Today' :
                     filter === 'last3days' ? '3 Days' :
                     filter === 'last7days' ? '7 Days' :
                     filter === 'last30days' ? '30 Days' : '45 Days'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards — single location keeps the classic boxes; multi shows
            one panel per location (metrics side by side, like the sketch) */}
        {selectedLocation && selectedLocationIds.length > 1 ? (
          <div className="row">
            {selectedLocationIds.map((locId) => {
              const loc = locations.find((l) => l.id === locId);
              const st = statsByLoc[locId] || { orders: 0, sales: 0, avgTicket: 0, itemsSold: 0 };
              return (
                <div className="col-lg-6 mb-3" key={locId}>
                  <div className="card" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                    <div style={{ background: 'linear-gradient(135deg, #ff6b35 0%, #ff8f35 100%)', padding: '12px 18px', color: '#fff' }}>
                      <div className="d-flex align-items-center">
                        <i className="fas fa-store mr-2"></i>
                        <span className="font-weight-bold">{loc?.companyName || loc?.locationName || 'Location'}</span>
                        {locId === selectedLocation.id && <span className="badge badge-light ml-2" style={{ fontSize: '0.65rem' }}>WORKING</span>}
                      </div>
                    </div>
                    <div className="card-body p-3">
                      <div className="row text-center">
                        <div className="col-6 mb-2">
                          <div className="text-muted" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Orders</div>
                          <div className="font-weight-bold" style={{ fontSize: '1.3rem', color: '#17a2b8' }}>{statsLoading ? '…' : st.orders}</div>
                        </div>
                        <div className="col-6 mb-2">
                          <div className="text-muted" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Sales</div>
                          <div className="font-weight-bold" style={{ fontSize: '1.3rem', color: '#28a745' }}>{statsLoading ? '…' : formatCurrency(st.sales)}</div>
                        </div>
                        <div className="col-6">
                          <div className="text-muted" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Avg Ticket</div>
                          <div className="font-weight-bold" style={{ fontSize: '1.3rem', color: '#e0a800' }}>{statsLoading ? '…' : formatCurrency(st.avgTicket)}</div>
                        </div>
                        <div className="col-6">
                          <div className="text-muted" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Items Sold</div>
                          <div className="font-weight-bold" style={{ fontSize: '1.3rem', color: '#dc3545' }}>{statsLoading ? '…' : st.itemsSold}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {/* Combined totals strip */}
            <div className="col-12">
              <div className="d-flex flex-wrap align-items-center justify-content-center" style={{ gap: 16, background: '#1f2937', borderRadius: 12, padding: '10px 18px', color: '#fff' }}>
                <span className="font-weight-bold" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1, color: '#9ca3af' }}>Combined</span>
                <span><i className="fas fa-shopping-cart mr-1" style={{ color: '#17a2b8' }}></i><b>{todayOrders}</b> orders</span>
                <span><i className="fas fa-dollar-sign mr-1" style={{ color: '#28a745' }}></i><b>{formatCurrency(todaySales)}</b> sales</span>
                <span><i className="fas fa-receipt mr-1" style={{ color: '#e0a800' }}></i><b>{formatCurrency(avgTicketSize)}</b> avg</span>
                <span><i className="fas fa-shopping-basket mr-1" style={{ color: '#dc3545' }}></i><b>{totalItemsSold}</b> items</span>
              </div>
            </div>
          </div>
        ) : selectedLocation && (
          <div className="row">
            <div className="col-lg-3 col-6">
              <div className="small-box bg-info">
                <div className="inner">
                  {statsLoading ? (
                    <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 24, height: 24, objectFit: 'contain' }} />
                  ) : (
                    <h3>{todayOrders}</h3>
                  )}
                  <p>Orders</p>
                </div>
                <div className="icon">
                  <i className="fas fa-shopping-cart"></i>
                </div>
                <a href="#" className="small-box-footer">More info <i className="fas fa-arrow-circle-right"></i></a>
              </div>
            </div>

            <div className="col-lg-3 col-6">
              <div className="small-box bg-success">
                <div className="inner">
                  {statsLoading ? (
                    <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 24, height: 24, objectFit: 'contain' }} />
                  ) : (
                    <h3>{formatCurrency(todaySales)}</h3>
                  )}
                  <p>Sales</p>
                </div>
                <div className="icon">
                  <i className="fas fa-dollar-sign"></i>
                </div>
                <a href="#" className="small-box-footer">More info <i className="fas fa-arrow-circle-right"></i></a>
              </div>
            </div>

            <div className="col-lg-3 col-6">
              <div className="small-box bg-warning">
                <div className="inner">
                  {statsLoading ? (
                    <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 24, height: 24, objectFit: 'contain' }} />
                  ) : (
                    <h3>{formatCurrency(avgTicketSize)}</h3>
                  )}
                  <p>Avg Ticket</p>
                </div>
                <div className="icon">
                  <i className="fas fa-receipt"></i>
                </div>
                <a href="#" className="small-box-footer">More info <i className="fas fa-arrow-circle-right"></i></a>
              </div>
            </div>

            <div className="col-lg-3 col-6">
              <div className="small-box bg-danger">
                <div className="inner">
                  {statsLoading ? (
                    <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 24, height: 24, objectFit: 'contain' }} />
                  ) : (
                    <h3>{totalItemsSold}</h3>
                  )}
                  <p>Items Sold</p>
                </div>
                <div className="icon">
                  <i className="fas fa-shopping-basket"></i>
                </div>
                <a href="#" className="small-box-footer">More info <i className="fas fa-arrow-circle-right"></i></a>
              </div>
            </div>
          </div>
        )}

        {/* Fody Flight AI Banner */}
        {selectedLocation && (
          <div className="row mb-4">
            <div className="col-12">
              <div
                className="d-flex align-items-center justify-content-between"
                style={{
                  background: 'linear-gradient(135deg, #fff5f2 0%, #ffffff 100%)',
                  borderRadius: '16px',
                  padding: '24px 28px',
                  border: '1px solid rgba(255, 107, 53, 0.15)',
                  boxShadow: '0 4px 20px rgba(255, 107, 53, 0.08)',
                }}
              >
                <div className="d-flex align-items-center" style={{ gap: '18px' }}>
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: '16px',
                      background: 'linear-gradient(135deg, #ff6b35 0%, #ff8f35 100%)',
                      boxShadow: '0 4px 12px rgba(255, 107, 53, 0.3)',
                    }}
                  >
                    <img src="/fodyAI.png" alt="Fody" className="fody-shine" style={{ width: 28, height: 28, objectFit: 'contain' }} />
                  </div>
                  <div>
                    <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                      <h5 className="m-0 font-weight-bold" style={{ color: '#2d2d2d', fontSize: '1.1rem' }}>
                        Meet Fody Flight
                      </h5>
                      <span
                        style={{
                          fontSize: '0.6rem',
                          fontWeight: 700,
                          color: '#FF5722',
                          background: 'rgba(255, 87, 34, 0.12)',
                          padding: '2px 6px',
                          borderRadius: 6,
                          border: '1px solid rgba(255, 87, 34, 0.25)',
                          textTransform: 'uppercase',
                        }}
                      >
                        BETA
                      </span>
                    </div>
                    <p className="m-0 text-muted" style={{ fontSize: '0.9rem', marginTop: '4px' }}>
                      Your AI assistant — generate reports, get insights, and grow your business in seconds.
                    </p>
                  </div>
                </div>
                <button
                  className="btn font-weight-bold"
                  style={{
                    background: 'linear-gradient(135deg, #ff6b35 0%, #ff8f35 100%)',
                    color: 'white',
                    borderRadius: '12px',
                    padding: '10px 22px',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(255, 107, 53, 0.3)',
                    transition: 'transform 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.04)')}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                  onClick={() => navigate('/admin/fody-flight')}
                >
                  <i className="fas fa-sparkles mr-2"></i>
                  Ask Fody Flight
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sales Overview Chart */}
        {selectedLocation && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div className="d-flex justify-content-between align-items-center flex-wrap">
                    <h3 className="card-title">Sales Overview</h3>
                    <div className="d-flex align-items-center" style={{ gap: 12 }}>
                      {selectedLocationIds.length > 1 && (
                        <div className="btn-group btn-group-sm">
                          <button className={`btn ${chartMode === 'combined' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setChartMode('combined')}>Combined</button>
                          <button className={`btn ${chartMode === 'individual' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setChartMode('individual')}>Individual</button>
                        </div>
                      )}
                      {salesData.length > 0 && chartMode === 'combined' && (
                        <span className="text-orange font-weight-bold">
                          {selectedLocationIds.length > 1 ? 'Total ' : ''}{formatCurrency(salesData.reduce((sum, item) => sum + item.amount, 0))}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Individual mode: per-location legend with its total */}
                  {selectedLocationIds.length > 1 && chartMode === 'individual' && (
                    <div className="d-flex flex-wrap mt-2" style={{ gap: 14 }}>
                      {selectedLocationIds.map((locId, i) => {
                        const loc = locations.find((l) => l.id === locId);
                        const series = chartByLoc[locId] || [];
                        const total = series.reduce((a, p) => a + p.amount, 0);
                        return (
                          <span key={locId} className="d-flex align-items-center" style={{ fontSize: '0.8rem' }}>
                            <span style={{ width: 10, height: 10, borderRadius: '50%', background: LOC_COLORS[i % LOC_COLORS.length], display: 'inline-block', marginRight: 6 }}></span>
                            <b>{loc?.companyName || loc?.locationName || 'Location'}</b>&nbsp;— {formatCurrency(total)}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="card-body">
                  {isLoadingChart ? (
                  <div className="text-center py-5">
                    <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
                  </div>
                  ) : salesData.length === 0 || salesData.every(d => d.amount === 0) ? (
                    <div className="text-center py-5">
                      <i className="fas fa-receipt fa-3x text-muted mb-3"></i>
                      <p className="text-muted">No sales data in this period</p>
                    </div>
                  ) : chartMode === 'individual' && selectedLocationIds.length > 1 ? (
                    <SalesMultiLineChart chartByLoc={chartByLoc} locationIds={selectedLocationIds} />
                  ) : (
                    <SalesLineChart data={salesData} />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recent Orders */}
        {selectedLocation && (
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Recent Orders</h3>
                </div>
                <div className="card-body">
                  {loadingOrders && recentOrders.length === 0 ? (
                    <div className="text-center py-4">
                      <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
                      <p className="text-muted mt-2">Loading orders...</p>
                    </div>
                  ) : recentOrders.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-muted">No orders found</p>
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <table className="table table-bordered table-hover">
                          <thead>
                            <tr>
                              <th>Order #</th>
                              <th>Confirmation #</th>
                              <th>Customer</th>
                              <th>Date</th>
                              <th>Status</th>
                              <th>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {recentOrders.map((order) => (
                              <tr
                                key={order.id}
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                  setSelectedOrder(order);
                                  setShowOrderModal(true);
                                }}
                              >
                                <td>{order.orderNumber}</td>
                                <td>{order.confirmationNumber}{order.guestNumber ? ` · Guest #${order.guestNumber}` : ''}</td>
                                <td>{order.customerName}</td>
                                <td>{formatDate(order.orderDate)}</td>
                                <td>
                                  <span className={`badge badge-${order.status === 'completed' ? 'success' : order.status === 'pending' ? 'warning' : 'secondary'}`}>
                                    {order.status}
                                  </span>
                                </td>
                                <td>{formatCurrency(order.total)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {hasMoreOrders && (
                        <div className="text-center mt-3">
                          <button 
                            className="btn btn-outline-primary"
                            onClick={() => loadRecentOrders(false)}
                            disabled={loadingOrders}
                          >
                            {loadingOrders ? (
                              <><i className="fas fa-spinner fa-spin mr-2"></i>Loading...</>
                            ) : (
                              <><i className="fas fa-plus mr-2"></i>Load More</>
                            )}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* No Location State */}
        {!selectedLocation && (
          <div className="text-center py-5">
            <i className="fas fa-store-slash fa-4x text-muted mb-4"></i>
            <h4 className="text-muted">No locations configured</h4>
            <p className="text-muted">Add a location to start viewing dashboard data</p>
            <button 
              className="btn btn-primary btn-lg"
              onClick={() => navigate('/admin/locations/new')}
            >
              <i className="fas fa-plus mr-2"></i>Add Location
            </button>
          </div>
        )}
      </div>

      {/* Location Picker Modal */}
      {showLocationPicker && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title font-weight-bold">Select Locations <small className="text-muted font-weight-normal">(combine metrics)</small></h5>
                <button type="button" className="close" onClick={() => setShowLocationPicker(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-2">Pick one or more — the dashboard combines their metrics. The first selected drives the rest of the console.</p>
                {locations.map((location) => {
                  const checked = selectedLocationIds.includes(location.id);
                  return (
                  <div
                    key={location.id}
                    className={`list-group-item d-flex justify-content-between align-items-center ${checked ? 'active' : ''}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleDashboardLocation(location)}
                  >
                    <div className="d-flex align-items-center">
                      <div
                        className="d-flex align-items-center justify-content-center mr-3"
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 8,
                          background: checked ? 'rgba(255,107,53,0.2)' : '#f8f9fa',
                          color: checked ? '#ff6b35' : '#6c757d',
                        }}
                      >
                        <i className="fas fa-store"></i>
                      </div>
                      <div>
                        <h6 className="m-0 font-weight-bold">{location.companyName || location.locationName}</h6>
                        <small className="text-muted">
                          {location.address || ''}, {location.city || ''}
                        </small>
                      </div>
                    </div>
                    <div className="custom-control custom-checkbox">
                      <input type="checkbox" className="custom-control-input" checked={checked} readOnly id={`locpick-${location.id}`} />
                      <label className="custom-control-label" htmlFor={`locpick-${location.id}`}></label>
                    </div>
                  </div>
                  );
                })}
                
                <button
                  className="btn btn-outline-primary btn-block mt-3"
                  onClick={() => {
                    setShowLocationPicker(false);
                    navigate('/admin/locations/new');
                  }}
                >
                  <i className="fas fa-plus mr-2"></i>Add New Location
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Order Detail Modal */}
      {showOrderModal && selectedOrder && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title font-weight-bold">
                  Order #{selectedOrder.confirmationNumber}
                  {selectedOrder.guestNumber ? ` · Guest #${selectedOrder.guestNumber}` : ''}
                </h5>
                <button type="button" className="close" onClick={() => setShowOrderModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-6">
                    <small className="text-muted">Order Number</small>
                    <div className="font-weight-bold">{selectedOrder.orderNumber}</div>
                  </div>
                  <div className="col-6 text-right">
                    <small className="text-muted">Date</small>
                    <div className="font-weight-bold">{formatDate(selectedOrder.orderDate)}</div>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-6">
                    <small className="text-muted">Customer</small>
                    <div className="font-weight-bold">{selectedOrder.customerName}</div>
                  </div>
                  <div className="col-6 text-right">
                    <small className="text-muted">Total</small>
                    <div className="font-weight-bold text-orange">{formatCurrency(selectedOrder.total)}</div>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-6">
                    <small className="text-muted">Status</small>
                    <div>
                      <span className={`badge badge-${selectedOrder.status === 'completed' ? 'success' : selectedOrder.status === 'pending' ? 'warning' : 'secondary'}`}>
                        {selectedOrder.status}
                      </span>
                    </div>
                  </div>
                </div>
                <hr />
                <div className="d-flex justify-content-between">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setShowOrderModal(false)}
                  >
                    Close
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setShowOrderModal(false);
                      navigate('/admin/sales');
                    }}
                  >
                    <i className="fas fa-external-link-alt mr-2"></i>
                    View in Sales
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminDashboardPage;